<script setup>
/* eslint-disable no-unused-vars */
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { usePolicy } from 'dashboard/composables/usePolicy';

import SidebarProfileMenu from 'dashboard/components-next/sidebar/SidebarProfileMenu.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import TopBarDropdown from './TopBarDropdown.vue';
import NotificationDropdown from './NotificationDropdown.vue';
import AgendaDropdown from './AgendaDropdown.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';

const emit = defineEmits(['openKeyShortcutModal']);

const { accountScopedRoute } = useAccount();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const searchShortcut = useKbd([`$mod`, 'k']);
const { shouldShow } = usePolicy();

// Check if the user has access to a given route link
const isAllowed = link => {
  if (!link) return true;
  const resolved = router.resolve(link);
  const permissions = resolved?.meta?.permissions ?? [];
  const featureFlag = resolved?.meta?.featureFlag || '';
  const installationTypes = resolved?.meta?.installationTypes || [];
  return shouldShow(featureFlag, permissions, installationTypes);
};

// Filter sections: remove items user can't access, then remove empty sections
const filterSections = sections => {
  return sections
    .map(s => ({ ...s, items: s.items.filter(i => isAllowed(i.link)) }))
    .filter(s => s.items.length > 0);
};

// Page title based on current route
const pageTitle = computed(() => {
  const nameMap = {
    // Home
    dashboard_home: t('SIDEBAR.HOME'),
    // Conversations
    home: t('SIDEBAR.CONVERSATIONS'),
    inbox_conversation: t('SIDEBAR.CONVERSATIONS'),
    conversation_through_mentions: t('SIDEBAR.CONVERSATIONS'),
    conversation_through_unattended: t('SIDEBAR.CONVERSATIONS'),
    conversations_through_label: t('SIDEBAR.CONVERSATIONS'),
    conversations_through_team: t('SIDEBAR.CONVERSATIONS'),
    conversation_through_inbox: t('SIDEBAR.CONVERSATIONS'),
    conversations_through_folders: t('SIDEBAR.CONVERSATIONS'),
    conversation_participating: t('SIDEBAR.CONVERSATIONS'),
    conversation_through_participating: t('SIDEBAR.CONVERSATIONS'),
    inbox_view: t('TOPBAR.INBOX_VIEW'),
    inbox_view_conversation: t('TOPBAR.INBOX_VIEW'),
    // Contacts
    contacts_dashboard_index: t('SIDEBAR.CONTACTS'),
    contacts_edit: t('SIDEBAR.CONTACTS'),
    contacts_dashboard_active: t('SIDEBAR.CONTACTS'),
    contacts_dashboard_segments_index: t('SIDEBAR.CONTACTS'),
    contacts_dashboard_labels_index: t('SIDEBAR.CONTACTS'),
    contacts_edit_segment: t('SIDEBAR.CONTACTS'),
    contacts_edit_label: t('SIDEBAR.CONTACTS'),
    // Segments & Conversions
    segments_index: t('SIDEBAR.SEGMENTS'),
    segment_detail: t('SIDEBAR.SEGMENTS'),
    conversions_index: t('SIDEBAR.CONVERSIONS'),
    conversion_detail: t('SIDEBAR.CONVERSIONS'),
    // Forms
    forms_index: t('SIDEBAR.FORMS'),
    forms_new: t('SIDEBAR.FORMS'),
    forms_edit: t('SIDEBAR.FORMS'),
    forms_submissions: t('SIDEBAR.FORMS'),
    // CRM
    crm_board: t('SIDEBAR.CRM'),
    crm_item_detail: t('SIDEBAR.CRM'),
    crm_list: t('SIDEBAR.CRM'),
    crm_calendar: t('SIDEBAR.CRM'),
    crm_funnels: 'Pipelines',
    crm_funnel_new: 'Pipelines',
    crm_funnel_edit: 'Pipelines',
    crm_settings: t('TOPBAR.CRM_SETTINGS'),
    crm_products: t('SIDEBAR.PRODUCTS'),
    crm_product_new: t('SIDEBAR.PRODUCTS'),
    crm_product_edit: t('SIDEBAR.PRODUCTS'),
    crm_tasks: t('SIDEBAR.TASKS'),
    crm_tickets_board: t('SIDEBAR.TICKETS'),
    crm_ticket_detail: t('SIDEBAR.TICKETS'),
    crm_tickets_list: t('SIDEBAR.TICKETS'),
    crm_tickets_calendar: t('SIDEBAR.TICKETS'),
    // Companies
    companies_dashboard_index: t('SIDEBAR.COMPANIES'),
    companies_detail: t('SIDEBAR.COMPANIES'),
    // Tools
    canned_list: t('SIDEBAR.CANNED_RESPONSES'),
    macros_wrapper: t('SIDEBAR.MACROS'),
    macros_new: t('SIDEBAR.MACROS'),
    macros_edit: t('SIDEBAR.MACROS'),
    workflows_index: 'Workflows',
    workflow_editor: 'Workflows',
    workflow_new: 'Workflows',
    labels_list: t('SIDEBAR.LABELS'),
    // Reports
    account_overview_reports: t('SIDEBAR.REPORTS'),
    conversation_reports: t('SIDEBAR.REPORTS'),
    agent_reports_index: t('SIDEBAR.REPORTS'),
    agent_reports_show: t('SIDEBAR.REPORTS'),
    label_reports_index: t('SIDEBAR.REPORTS'),
    label_reports_show: t('SIDEBAR.REPORTS'),
    inbox_reports_index: t('SIDEBAR.REPORTS'),
    inbox_reports_show: t('SIDEBAR.REPORTS'),
    team_reports_index: t('SIDEBAR.REPORTS'),
    team_reports_show: t('SIDEBAR.REPORTS'),
    csat_reports: t('SIDEBAR.REPORTS'),
    sla_reports: t('SIDEBAR.REPORTS'),
    bot_reports: t('SIDEBAR.REPORTS'),
    crm_reports: t('SIDEBAR.REPORTS'),
    // Settings
    general_settings_index: t('SIDEBAR.SETTINGS'),
    agent_list: t('SIDEBAR.SETTINGS'),
    settings_teams_list: t('SIDEBAR.SETTINGS'),
    settings_teams_new: t('SIDEBAR.SETTINGS'),
    settings_teams_edit: t('SIDEBAR.SETTINGS'),
    settings_inbox_list: t('SIDEBAR.SETTINGS'),
    settings_inbox_new: t('SIDEBAR.SETTINGS'),
    settings_inbox_show: t('SIDEBAR.SETTINGS'),
    agent_bots: t('SIDEBAR.SETTINGS'),
    sla_list: t('SIDEBAR.SETTINGS'),
    attributes_list: t('SIDEBAR.SETTINGS'),
    custom_roles_list: t('SIDEBAR.SETTINGS'),
    assignment_policy_index: t('SIDEBAR.SETTINGS'),
    conversation_workflow_index: t('SIDEBAR.SETTINGS'),
    security_settings_index: t('SIDEBAR.SETTINGS'),
    billing_settings_index: t('SIDEBAR.SETTINGS'),
    auditlogs_list: t('SIDEBAR.SETTINGS'),
    settings_applications: t('SIDEBAR.SETTINGS'),
    settings_integrations_webhook: t('SIDEBAR.SETTINGS'),
    settings_integrations_dashboard_apps: t('SIDEBAR.SETTINGS'),
    settings_integrations_scripts: t('SIDEBAR.SETTINGS'),
    // Broadcasts
    broadcasts_index: t('TOPBAR.BROADCASTS'),
    broadcasts_new: t('TOPBAR.BROADCAST_NEW'),
    broadcasts_show: t('TOPBAR.BROADCAST_SHOW'),
    // E-mail MKT (dobrado em Campanhas)
    email_mkt_index: t('TOPBAR.CAMPAIGNS'),
    email_mkt_new: t('TOPBAR.CAMPAIGN_NEW'),
    email_mkt_edit: t('TOPBAR.CAMPAIGN_EDIT'),
    email_mkt_show: t('TOPBAR.CAMPAIGN_REPORT'),
    // Campanhas
    campaigns_index: t('TOPBAR.CAMPAIGNS'),
    campaigns_new: t('TOPBAR.CAMPAIGN_NEW'),
    campaigns_settings: t('SIDEBAR.SETTINGS'),
    // Help Center
    portals_index: t('TOPBAR.HELP_CENTER'),
    portals_new: t('TOPBAR.HELP_CENTER'),
    portals_articles_index: t('TOPBAR.HELP_CENTER'),
    portals_articles_new: t('TOPBAR.HELP_CENTER'),
    portals_articles_edit: t('TOPBAR.HELP_CENTER'),
    portals_categories_index: t('TOPBAR.HELP_CENTER'),
    portals_settings_index: t('TOPBAR.HELP_CENTER'),
    // brain
    captain_assistants_index: 'brain',
    captain_assistants_create_index: 'brain',
    captain_assistants_responses_index: 'brain',
    captain_assistants_documents_index: 'brain',
    captain_tools_index: 'brain',
    captain_assistants_settings_index: 'brain',
    captain_assistants_scripts_index: 'brain',
    captain_assistants_skills_index: 'brain',
    captain_settings_index: 'brain',
    brain_assistants_index: 'brain',
    brain_insights: 'brain',
    brain_wizard_identity: 'brain',
    brain_wizard_scripts: 'brain',
    brain_wizard_knowledge: 'brain',
    brain_wizard_skills: 'brain',
    brain_wizard_messages: 'brain',
    brain_wizard_model: 'brain',
    brain_wizard_connect: 'brain',
    // Other
    notifications_index: t('TOPBAR.NOTIFICATIONS'),
    search: t('TOPBAR.SEARCH'),
    profile_settings_index: t('TOPBAR.PROFILE'),
    profile_settings_mfa: t('TOPBAR.PROFILE'),
  };
  return nameMap[route.name] || '';
});

// --- Dropdown Menu Definitions ---

const reportsSections = computed(() =>
  filterSections([
    {
      title: t('TOPBAR.OVERVIEW'),
      items: [
        {
          label: t('TOPBAR.LIVE_DASHBOARD'),
          icon: 'i-lucide-activity',
          link: accountScopedRoute('account_overview_reports'),
        },
        {
          label: t('SIDEBAR.CONVERSATIONS'),
          icon: 'i-lucide-message-square',
          link: accountScopedRoute('conversation_reports'),
        },
      ],
    },
    {
      title: t('TOPBAR.PERFORMANCE'),
      items: [
        {
          label: t('TOPBAR.AGENTS'),
          icon: 'i-lucide-user',
          link: accountScopedRoute('agent_reports_index'),
        },
        {
          label: t('TOPBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          link: accountScopedRoute('inbox_reports_index'),
        },
        {
          label: t('TOPBAR.TEAMS'),
          icon: 'i-lucide-users',
          link: accountScopedRoute('team_reports_index'),
        },
        {
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          link: accountScopedRoute('label_reports_index'),
        },
      ],
    },
    {
      title: t('TOPBAR.SATISFACTION_SLA'),
      items: [
        {
          label: 'CSAT',
          icon: 'i-lucide-smile',
          link: accountScopedRoute('csat_reports'),
        },
        {
          label: 'SLA',
          icon: 'i-lucide-timer',
          link: accountScopedRoute('sla_reports'),
        },
      ],
    },
    {
      title: t('TOPBAR.OTHERS'),
      items: [
        {
          label: 'Bot',
          icon: 'i-lucide-bot',
          link: accountScopedRoute('bot_reports'),
        },
        {
          label: t('SIDEBAR.CRM'),
          icon: 'i-lucide-briefcase',
          link: accountScopedRoute('crm_reports'),
        },
      ],
    },
  ])
);

const captainSections = computed(() =>
  filterSections([
    {
      title: '',
      items: [
        {
          label: t('TOPBAR.ASSISTANTS'),
          icon: 'i-ph-sparkle-fill',
          link: accountScopedRoute('brain_assistants_index'),
        },
        {
          label: t('SIDEBAR.SETTINGS'),
          icon: 'i-lucide-settings',
          link: accountScopedRoute('captain_settings_index'),
        },
      ],
    },
  ])
);

const broadcastsSections = computed(() =>
  filterSections([
    {
      title: '',
      items: [
        {
          label: t('TOPBAR.CAMPAIGNS'),
          icon: 'i-lucide-list',
          link: accountScopedRoute('campaigns_index'),
        },
        {
          label: t('TOPBAR.CAMPAIGN_NEW'),
          icon: 'i-lucide-plus',
          link: accountScopedRoute('campaigns_new'),
        },
        {
          label: t('SIDEBAR.SETTINGS'),
          icon: 'i-lucide-settings-2',
          link: accountScopedRoute('campaigns_settings'),
        },
      ],
    },
  ])
);

const helpCenterSections = computed(() =>
  filterSections([
    {
      title: '',
      items: [
        {
          label: t('TOPBAR.PORTALS'),
          icon: 'i-lucide-library-big',
          link: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
      ],
    },
  ])
);

const settingsSections = computed(() =>
  filterSections([
    {
      title: t('TOPBAR.GENERAL'),
      items: [
        {
          label: t('TOPBAR.ACCOUNT'),
          icon: 'i-lucide-building-2',
          link: accountScopedRoute('general_settings_index'),
        },
        {
          label: t('TOPBAR.AGENTS'),
          icon: 'i-lucide-user',
          link: accountScopedRoute('agent_list'),
        },
        {
          label: t('TOPBAR.TEAMS'),
          icon: 'i-lucide-users',
          link: accountScopedRoute('settings_teams_list'),
        },
        {
          label: t('TOPBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          link: accountScopedRoute('settings_inbox_list'),
        },
      ],
    },
    {
      title: t('SIDEBAR.CRM'),
      items: [
        {
          label: 'Pipelines',
          icon: 'i-lucide-git-branch',
          link: accountScopedRoute('crm_funnels'),
        },
      ],
    },
    {
      title: t('TOPBAR.AUTOMATION_RULES'),
      items: [
        {
          label: 'Workflows',
          icon: 'i-lucide-workflow',
          link: accountScopedRoute('workflows_index'),
        },
        {
          label: 'Bots',
          icon: 'i-lucide-bot',
          link: accountScopedRoute('agent_bots'),
        },
        {
          label: 'SLA',
          icon: 'i-lucide-timer',
          link: accountScopedRoute('sla_list'),
        },
        {
          label: t('TOPBAR.SCHEDULES'),
          icon: 'i-lucide-calendar-clock',
          link: accountScopedRoute('scheduled_messages_list'),
        },
      ],
    },
    {
      title: t('TOPBAR.ADVANCED'),
      items: [
        {
          label: t('TOPBAR.INTEGRATIONS'),
          icon: 'i-lucide-puzzle',
          link: accountScopedRoute('settings_applications'),
        },
        {
          label: 'Webhooks',
          icon: 'i-lucide-webhook',
          link: accountScopedRoute('settings_integrations_webhook'),
        },
        {
          label: t('TOPBAR.ADS_MANAGER'),
          icon: 'i-lucide-megaphone',
          link: accountScopedRoute('settings_meta_ads'),
        },
        {
          label: t('TOPBAR.ATTRIBUTES'),
          icon: 'i-lucide-list',
          link: accountScopedRoute('attributes_list'),
        },
        {
          label: t('TOPBAR.ROLES'),
          icon: 'i-lucide-shield',
          link: accountScopedRoute('custom_roles_list'),
        },
        {
          label: t('TOPBAR.ASSIGNMENT_POLICY'),
          icon: 'i-lucide-user-check',
          link: accountScopedRoute('assignment_policy_index'),
        },
        {
          label: t('TOPBAR.SECURITY'),
          icon: 'i-lucide-lock',
          link: accountScopedRoute('security_settings_index'),
        },
        {
          label: t('TOPBAR.AUDIT'),
          icon: 'i-lucide-shield-check',
          link: accountScopedRoute('auditlogs_list'),
        },
      ],
    },
  ])
);

// Theme toggle
const isDark = ref(document.body.classList.contains('dark'));

const toggleTheme = () => {
  const newTheme = isDark.value ? 'light' : 'dark';
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, newTheme);
  const isOSOnDarkMode = window.matchMedia(
    '(prefers-color-scheme: dark)'
  ).matches;
  setColorTheme(isOSOnDarkMode);
  isDark.value = document.body.classList.contains('dark');
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <header
    class="flex items-center justify-between h-12 px-2 lg:px-4 shadow-[0_2px_6px_rgba(0,0,0,0.04)] dark:shadow-[0_2px_6px_rgba(0,0,0,0.3)] bg-n-s2-nav-bg flex-shrink-0 z-30"
  >
    <!-- Left: Page Title + Search Bar -->
    <div class="flex items-center gap-2 lg:gap-4 min-w-0 flex-1">
      <h1
        v-if="pageTitle"
        class="text-[13px] lg:text-[15px] font-semibold text-n-s2-nav-text-hover truncate m-0 flex-shrink min-w-0"
      >
        <span v-if="pageTitle === 'brain'" class="brain-wordmark">
          br<span class="brain-highlight">ai</span>n
        </span>
        <template v-else>{{ pageTitle }}</template>
      </h1>
      <!-- Desktop: full search bar -->
      <div class="hidden lg:flex max-w-[320px] w-full">
        <RouterLink
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-3 h-8 w-full rounded-lg border border-n-s2-nav-border bg-white dark:bg-n-solid-2 hover:border-n-brand/30 transition-all duration-150"
        >
          <span class="i-lucide-search size-3.5 text-n-s2-nav-muted" />
          <span class="text-[13px] text-n-s2-nav-muted">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="ml-auto text-[10px] font-semibold text-n-s2-nav-muted bg-white dark:bg-n-solid-3 border border-n-s2-nav-border rounded px-1 py-px"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
      </div>
      <!-- Mobile: search icon -->
      <RouterLink
        :to="{ name: 'search' }"
        class="lg:hidden size-8 rounded-lg hover:bg-n-s2-nav-bg-hover grid place-content-center text-n-s2-nav-text hover:text-n-s2-nav-text-hover transition-colors flex-shrink-0"
        :title="t('TOPBAR.SEARCH')"
      >
        <span class="i-lucide-search size-4" />
      </RouterLink>
    </div>

    <!-- Right: Dropdowns + Actions -->
    <div class="flex items-center gap-0.5">
      <!-- Dropdown Menus — permission-filtered, hide on small screens -->
      <TopBarDropdown
        v-if="reportsSections.length"
        class="hidden lg:block"
        :label="t('SIDEBAR.REPORTS')"
        icon="i-lucide-chart-spline"
        :sections="reportsSections"
      />
      <TopBarDropdown
        v-if="captainSections.length"
        class="hidden xl:block"
        label="brain"
        icon="i-ph-sparkle-fill"
        :sections="captainSections"
      >
        <template #label>
          <span class="brain-wordmark"
            >br<span class="brain-highlight">ai</span>n</span
          >
        </template>
      </TopBarDropdown>
      <TopBarDropdown
        v-if="broadcastsSections.length"
        class="hidden lg:block"
        :label="t('TOPBAR.CAMPAIGNS')"
        icon="i-lucide-rocket"
        :sections="broadcastsSections"
      />
      <TopBarDropdown
        v-if="settingsSections.length"
        :label="t('TOPBAR.NAV_SETTINGS')"
        icon="i-lucide-settings"
        :sections="settingsSections"
        width="w-64"
      />

      <!-- Separator -->
      <div class="w-px h-5 bg-n-s2-nav-border mx-1.5 hidden sm:block" />

      <!-- Compose -->
      <ComposeConversation align-position="left" @close="onComposeClose">
        <template #trigger="{ toggle }">
          <button
            class="size-8 rounded-lg hover:bg-n-s2-nav-bg-hover grid place-content-center text-n-s2-nav-text hover:text-n-s2-nav-text-hover transition-colors cursor-pointer"
            :title="t('TOPBAR.NEW_CONVERSATION')"
            @click="onComposeOpen(toggle)"
          >
            <span class="i-lucide-pen-line size-4" />
          </button>
        </template>
      </ComposeConversation>

      <!-- Agenda -->
      <AgendaDropdown />

      <!-- Notifications -->
      <NotificationDropdown />

      <!-- Theme Toggle -->
      <button
        class="size-8 rounded-lg hover:bg-n-s2-nav-bg-hover grid place-content-center text-n-s2-nav-text hover:text-n-s2-nav-text-hover transition-colors cursor-pointer"
        :title="isDark ? t('TOPBAR.LIGHT_MODE') : t('TOPBAR.DARK_MODE')"
        @click="toggleTheme"
      >
        <span
          class="size-4"
          :class="isDark ? 'i-lucide-sun' : 'i-lucide-moon'"
        />
      </button>

      <!-- Separator -->
      <div class="w-px h-5 bg-n-s2-nav-border mx-1.5" />

      <!-- Profile Avatar -->
      <SidebarProfileMenu
        variant="topbar"
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </div>
  </header>
</template>
